"use client";

import type { GameData } from "@/app/games/(details)/types";
import { Action } from "@/context/AnalyticsProvider";
import { useGamePageV3Analytics } from "@/context/AnalyticsProvider/gamePageV3";
import { cn } from "@/lib/utils";
import dynamic from "next/dynamic";
import Image from "next/image";
import { useCallback, useEffect, useState } from "react";
import type { OnProgressProps } from "react-player/base";

const ReactPlayer = dynamic(() => import("react-player/lazy"), { ssr: false });

interface VideoIntroProps {
  videoSrc?: string;
  accentColour: string | undefined;
  children: React.ReactNode;
  gameData?: GameData;
}

export const VideoIntro = ({
  videoSrc,
  accentColour,
  children,
  gameData,
}: VideoIntroProps) => {
  const { track } = useGamePageV3Analytics();
  const [videoEnded, setVideoEnded] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [isBuffering, setIsBuffering] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    if (!isBuffering) setTimeout(() => setIsPlaying(true), 150);
  }, [isBuffering]);

  useEffect(() => {
    // add overflow hidden to the body
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    document.body.style.setProperty(
      "--accent-colour",
      accentColour?.replace("rgb(", "").replace(")", "") || "81,255,0",
    );
  }, [accentColour]);

  const handleOnLoaded = useCallback(({ loaded }: OnProgressProps) => {
    if (loaded >= 1) setIsBuffering(false);
  }, []);

  const handleVideoEnd = useCallback(() => {
    track({
      action: Action.VIEWED,
      extras: {
        id: `video-intro-${gameData?.details.game_id}`,
        gameId: gameData?.details.game_id,
        gameName: gameData?.details.name,
      },
    });

    // First fade in the overlay
    setShowOverlay(true);

    // Then show the page content after a brief delay
    setTimeout(() => {
      setVideoEnded(true);
      setTimeout(() => {
        document.body.style.overflow = "";
      }, 700);
    }, 700); // Adjust timing as needed
  }, [gameData?.details.game_id, gameData?.details.name, track]);

  if (!videoSrc) return children;

  return (
    <>
      {/* Video - hide once ended */}
      <ReactPlayer
        url={videoSrc}
        playing={isPlaying}
        muted
        width="100%"
        height="100%"
        onProgress={handleOnLoaded}
        onEnded={handleVideoEnd}
        className="fixed inset-0 w-screen h-screen [&>video]:object-cover"
        style={{ zIndex: showOverlay ? 0 : 10 }}
        controls={false}
        playsInline
      />

      {/* Loading indicator - show while video is buffering */}
      <div
        className={cn(
          "w-screen h-screen bg-neutral-900 z-10 fixed pointer-events-none inset-0 flex items-center justify-center duration-500 transition-all",
          isBuffering ? "opacity-100 scale-100" : "opacity-0 scale-[10]",
        )}
      >
        <Image
          src="/images/immutable-loader.webp"
          alt="Loading"
          width={220}
          height={220}
          className="animate-pulse"
          priority
        />
      </div>

      {/* Overlay - fade in when video ends  */}
      <div
        className={cn(
          "fixed inset-0 bg-black/50 transition-opacity duration-500",
          showOverlay ? "opacity-100" : "opacity-0",
        )}
      />

      {/* Page Content - render after overlay shows, animate after overlay fades in  */}
      {showOverlay ? (
        <div
          className={cn(
            "flex w-full h-full transition-all duration-700",
            videoEnded
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-10 pointer-events-none",
          )}
        >
          {children}
        </div>
      ) : null}
    </>
  );
};
